import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import api from "./axiosconfig";
import {
  resetProfileImage,
  setProfileImagePath,
} from "./store/profileImageSlice";
import { fetchUserProfile, clearProfile } from "./store/profileSlice";
import { OnboardingProvider } from "./screens/Registerflow/contexts/OnboardingContext";
import { MindfulnessMusicProvider } from "./context/MindfulnessMusicContext";
import { loginSuccess } from "./store/authSlice";
import { useAuthSession } from "./hooks/auth/useAuthSession";

// Screens
import { TakeDeepBreath } from "./screens/TakeDeepBreath/TakeDeepBreath";
import { DashboardGridWith } from "./screens/Registerflow/Dashboard/Dashboard";
import { GetStartedPage } from "./screens/Registerflow/GetStartedPage/GetStartedPage";
import { LoginCode } from "./screens/Registerflow/LoginCode/LoginCode";
import { LoginPage } from "./screens/Registerflow/LoginPage/LoginPage";
import { ChatMain } from "./screens/chatMain";
import { ConfirmPassword } from "./screens/Registerflow/ConfirmPassword/ConfirmPassword";
import { DashboardGridWithChat } from "./screens/chatMain/ChatDashboard/ChatDashboard";
import { AllSetPage } from "./screens/Registerflow/AllSet/AllSetPage";
import { Header } from "./screens/Assessment/Header-Assesment/Header";
import { Disclamer } from "./screens/chatMain/Disclamer/Disclamer";
import { Mindfulness } from "./screens/MindfulnessExercises/Mindfulness/Mindfulness";
import { Onboarding1 } from "./screens/Registerflow/Onboarding-1/Onboarding-1";
import { Onboarding2 } from "./screens/Registerflow/Onboarding-2/Onboarding-2";
import { Onboarding3 } from "./screens/Registerflow/Onboarding-3/Onboarding-3";
import { Onboarding4 } from "./screens/Registerflow/Onboarding-4/Onboarding-4";
import { Onboarding5 } from "./screens/Registerflow/Onboarding-5/Onboarding-5";
import { Onboarding6 } from "./screens/Registerflow/Onboarding-6/Onboarding-6";
import { Onboarding7 } from "./screens/Registerflow/Onboarding-7/Onboarding-7";
import { TwoFA } from "./screens/Registerflow/TwoFA/TwoFA";
import { OnBoardingReminders } from "./screens/Registerflow/OnBoarding-Reminders/OnBoardingReminders";
import { Exercises } from "./screens/MindfulnessExercises/Exercises/Exercises";
import { AssessmentDashboard } from "./screens/Assessment/Assessment-dashboard/Assessment-dashboard";
import { PhysicalDashboard } from "./screens/Assessment/Physical-Assessment/Physical-Assessment";
import { PEARLIReports } from "./screens/Assessment/Reports/PEARLIReports";
import { PhysicalDomain } from "./screens/Assessment/Components/PhysicalDomain";
import { MainContentDisclaimer } from "./screens/Assessment/Disclaimer/MainContentDisclaimer";
import { Profile } from "./screens/Profile/Profile";
import { Edit } from "./screens/Profile/Edit/Edit";
import { Password } from "./screens/Profile/Password/Password";
import { Language } from "./screens/Profile/Language/Language";
import { Notification } from "./screens/Profile/Notification/Notification";
import { AdminIndex } from "./screens/Admin/AdminIndex";
import { Analytics } from "./screens/Admin/Analytics/Analytics";
import { AssessmentAnalytics } from "./pages/assessment/AssessmentAnalytics";
import { UserManagement } from "./screens/Admin/UserManagement/UserManagement";
import { LiveMainView } from "./screens/Live/LiveMainView/LiveMainView";
import { LiveRegister } from "./screens/Live/LiveRegister/LiveRegister";
import { LiveJoinRedirect } from "./screens/Live/LiveJoin/LiveJoinRedirect";
import { HomeMaincontent } from "./screens/Home/HomeMaincontent";
import { Home } from "./screens/Home/Home";
import { TourPage } from "./screens/Home/TourPage";
import TourLanding from "./pages/TourLanding/TourLanding";
import { MobileSidebar } from "./screens/chatMain/Sidebar/SidebarMobile";
import { ResetPassword } from "./screens/Registerflow/ResetPassword/ResetPassword";
import { ResetForm } from "./screens/Registerflow/ResetForm/ResetForm";
import { RegisterCompany } from "./screens/Registerflow/RegisterCompany/RegisterCompany";
import { Pricing } from "./screens/Registerflow/Pricing/Pricing";
import { ConfirmBusiness } from "./screens/Registerflow/ConfirmBusiness/ConfirmBusiness";
import { TermsConditions } from "./screens/Registerflow/TermsConditions/TermsConditions";
import TermsOfUsePage from "./pages/Legal/TermsOfUsage";
import PrivacyPolicyPage from "./pages/Legal/privacy_policy/PrivacyPolicy";
import { Courses } from "./screens/Courses/Courses";
import PearliAssessment from "./pages/pearli/PearliAssessment";
import DevIndex from "./pages/dev/DevIndex";
import DevAddData from "./pages/dev/AddData";
import DevLiveEvents from "./pages/dev/liveEvents/DevLiveEvents";
import DevSendEmail from "./pages/dev/emailBroadcast/DevSendEmail";
import BugReportDialog from "./components/bug-report/BugReportDialog";
import {
  closeBugReport,
  selectIsBugReportOpen,
} from "./store/bugReportSlice";

const LEGAL_ROUTE_PATHS = new Set(["/PrivacyPolicy", "/TermsOfUsage"]);

const shouldBypassInitialLoading = () => {
  if (typeof window === "undefined") return false;

  const normalizedPath = window.location.pathname.replace(/\/+$/, "") || "/";
  return LEGAL_ROUTE_PATHS.has(normalizedPath);
};

const AppContent = () => {
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const user = useSelector((s) => s.auth.user);
  const isBugReportOpen = useSelector(selectIsBugReportOpen);

  const userId = user?.sub; 
  const userRole = user?.role;

  const dispatch = useDispatch();
  const API_URL = process.env.REACT_APP_API_URL;

  const isDevUser = isAuthenticated && userRole === "Developer";

  // Proactively monitor session token lifecycle, automatic silent refresh, and expiration
  useAuthSession();

  const handleBugReportSubmit = async (formData) => {
    try {
      await api.post("/api/bugreport", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      toast.success("Thanks for reporting! We’ll review it soon.");
    } catch (error) {
      console.error("Bug report failed:", error);
      toast.error("Could not send the report. Please try again later.");
    }
  };

  console.log("USER:", user);

  useEffect(() => {
    // Skip normal user logic for developer
    if (isDevUser) return;

    if (!isAuthenticated) {
      dispatch(resetProfileImage());
      dispatch(clearProfile());
      return;
    }

    let isMounted = true;

    const fetchProfile = async () => {
      try {
        dispatch(fetchUserProfile());

        const res = await api.post(`${API_URL}/profile`, {
          request: "getUserData",
        });

        if (!isMounted) return;

        if (res?.data?.result === 1 && res.data.data?.profilepicturepath) {
          dispatch(setProfileImagePath(res.data.data.profilepicturepath));
        } else {
          dispatch(resetProfileImage());
        }

        // Background pre-fetch reminder preferences on user login
        api.post(`${API_URL}/profile`, { request: "getReminderData" }).then((remRes) => {
          if (remRes.data && remRes.data.result === 1 && remRes.data.preferences) {
            const prefs = {
              weekly_guidance: remRes.data.preferences.weekly_guidance !== false,
              assessment: remRes.data.preferences.assessment !== false,
              checkins: remRes.data.preferences.checkins !== false,
              sherpa: remRes.data.preferences.sherpa !== false,
              live_events: remRes.data.preferences.live_events !== false,
            };
            localStorage.setItem("mindsherpa_notification_preferences", JSON.stringify(prefs));
          }
        }).catch(() => {});
      } catch (error) {
        console.warn("⚠️ Profile fetch failed:", error?.message || error);
        dispatch(resetProfileImage());
      }
    };

    fetchProfile();

    return () => {
      isMounted = false;
    };
  }, [isAuthenticated, isDevUser, userId, dispatch, API_URL]);

  // Routes for all users

  return (
    <Router>
      <MindfulnessMusicProvider>
        <Routes>
        <Route
          path="/"
          element={
            isAuthenticated ? <Navigate to="/home" replace /> : <DashboardGridWith />
          }
        />
        <Route
          path="/get-started"
          element={
            isAuthenticated ? <Navigate to="/home" replace /> : <GetStartedPage />
          }
        />
        <Route
          path="/login-code"
          element={
            isAuthenticated ? <Navigate to="/home" replace /> : <LoginCode />
          }
        />
        <Route
          path="/login"
          element={
            isAuthenticated ? <Navigate to="/home" replace /> : <LoginPage />
          }
        />
        <Route
          path="/sherpa"
          element={isAuthenticated ? <ChatMain /> : <Navigate to="/login" />}
        />
        <Route
          path="/sherpa/session/:sessionId"
          element={isAuthenticated ? <ChatMain /> : <Navigate to="/login" />}
        />
        <Route path="/password" element={<ConfirmPassword />} />
        <Route path="/password/:id" element={<ConfirmPassword />} />
        <Route path="/allset" element={<AllSetPage />} />
        <Route path="/Onboarding" element={<Onboarding1 />} />
        <Route path="/Onboarding-Header" element={<Onboarding2 />} />
        <Route path="/Onboarding-Header1" element={<Onboarding3 />} />
        <Route path="/Onboarding-Header2" element={<Onboarding4 />} />
        <Route path="/Onboarding-Header3" element={<Onboarding5 />} />
        <Route path="/Onboarding-Header4" element={<Onboarding6 />} />
        <Route path="/Onboarding-Header5" element={<Onboarding7 />} />
        <Route path="/TwoFA" element={<TwoFA />} />
        <Route
          path="/sherpa-dashboard"
          element={
            isAuthenticated ? <DashboardGridWithChat /> : <Navigate to="/login" />
          }
        />
        <Route path="/header" element={<Header />} />
        <Route path="/disclamer" element={<Disclamer />} />
        <Route
          path="/mindfulness"
          element={isAuthenticated ? <Mindfulness /> : <Navigate to="/login" />}
        />
        <Route
          path="/exercises/:id"
          element={isAuthenticated ? <Exercises /> : <Navigate to="/login" />}
        />
        <Route
          path="/assessment-dashboard"
          element={
            isAuthenticated ? <AssessmentDashboard /> : <Navigate to="/login" />
          }
        />
        <Route
          path="/assessment"
          element={<Navigate to="/assessment-dashboard" replace />}
        />
        <Route
          path="/PEARLIReports"
          element={isAuthenticated ? <PEARLIReports /> : <Navigate to="/login" />}
        />
        <Route
          path="/assessment-analytics"
          element={
            isAuthenticated ? <PearliAssessment /> : <Navigate to="/login" />
          }
        />
        <Route
          path="/Assessment/maincontent/:domain"
          element={
            isAuthenticated ? <PhysicalDashboard /> : <Navigate to="/login" />
          }
        />
        <Route path="/Question-card/:domain" element={<PhysicalDomain />} />
        <Route path="/Disclaimer" element={<MainContentDisclaimer />} />
        <Route
          path="/profile"
          element={isAuthenticated ? <Profile /> : <Navigate to="/login" />}
        />
        <Route
          path="/profile/edit"
          element={isAuthenticated ? <Edit /> : <Navigate to="/login" />}
        />
        <Route
          path="/profile/language"
          element={isAuthenticated ? <Language /> : <Navigate to="/login" />}
        />
        <Route
          path="/profile/password"
          element={isAuthenticated ? <Password /> : <Navigate to="/login" />}
        />
        <Route
          path="/profile/notification"
          element={isAuthenticated ? <Notification /> : <Navigate to="/login" />}
        />
        <Route path="/Onboarding-Reminders" element={<OnBoardingReminders />} />
        <Route
          path="/admin"
          element={isAuthenticated ? <AdminIndex /> : <Navigate to="/login" />}
        />
        <Route
          path="/admin/analytics"
          element={isAuthenticated ? <Analytics /> : <Navigate to="/login" />}
        />
        <Route
          path="/admin/assessment-analytics"
          element={isAuthenticated ? <AssessmentAnalytics /> : <Navigate to="/login" />}
        />
        <Route
          path="/admin/usermanagement"
          element={
            isAuthenticated ? <UserManagement /> : <Navigate to="/login" />
          }
        />
        <Route
          path="/live"
          element={isAuthenticated ? <LiveMainView /> : <Navigate to="/login" />}
        />
        <Route path="/live/register/:event/:ID" element={<LiveRegister />} />
        <Route path="/live/register/:ID" element={<LiveRegister />} />
        <Route path="/live/register/session/:ID" element={<LiveRegister />} />
        <Route
          path="/live/join/:ID"
          element={isAuthenticated ? <LiveJoinRedirect /> : <Navigate to="/login" />}
        />
        <Route path="/TakeDeepBreath" element={<TakeDeepBreath />} />
        <Route
          path="/user-statistics"
          element={isAuthenticated ? <Home /> : <Navigate to="/login" />}
        />
        <Route
          path="/HomeMaincontent"
          element={
            isAuthenticated ? <HomeMaincontent /> : <Navigate to="/login" />
          }
        />
        <Route path="/tour" element={<TourPage />} />
        <Route 
          path="/home" 
          element={isAuthenticated ? <TourLanding /> : <Navigate to="/login" />} 
        />
        <Route
          path="/dashboard"
          element={<Navigate to="/home" replace />}
        />
        <Route path="/SidebarMobile" element={<MobileSidebar />} />
        <Route path="/ResetPassword" element={<ResetPassword />} />
        <Route path="/ResetPassword/:id" element={<ResetForm />} />
        <Route path="/RegisterCompany" element={<RegisterCompany />} />
        <Route path="/Pricing" element={<Pricing />} />
        <Route path="/ConfirmBusiness" element={<ConfirmBusiness />} />
        <Route path="/termsConditions" element={<TermsConditions />} />
        <Route path="/TermsConditions" element={<TermsConditions />} />
        <Route path="/TermsOfUsage" element={<TermsOfUsePage />} />
        <Route path="/PrivacyPolicy" element={<PrivacyPolicyPage />} />
        <Route
          path="/Courses"
          element={isAuthenticated ? <Courses /> : <Navigate to="/login" />}
        />

        {/* Developer portal routes */}
        <Route
          path="/dev"
          element={isDevUser ? <DevIndex /> : <Navigate to="/login" />}
        />
        <Route
          path="/dev/addData"
          element={isDevUser ? <DevAddData /> : <Navigate to="/login" />}
        />
        <Route
          path="/dev/live-events"
          element={isDevUser ? <DevLiveEvents /> : <Navigate to="/login" />}
        />
        <Route
          path="/dev/send-email"
          element={isDevUser ? <DevSendEmail /> : <Navigate to="/login" />}
        />
      </Routes>

      {isAuthenticated && (
        <BugReportDialog
          open={isBugReportOpen}
          onClose={() => dispatch(closeBugReport())}
          onSubmit={handleBugReportSubmit}
        />
      )}
      </MindfulnessMusicProvider>
    </Router>
  );
};


export default function App() {
  const [isLoading, setIsLoading] = useState(() => {
    if (shouldBypassInitialLoading()) {
      return false;
    }

    return !sessionStorage.getItem("hasShownLoading");
  });


  useEffect(() => {
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);
      const token = params.get("token");
      if (token) {
        // Clean query parameters from URL immediately without page reload
        const url = new URL(window.location.href);
        url.searchParams.delete("token");
        url.searchParams.delete("refreshToken");
        window.history.replaceState({}, document.title, url.pathname + url.search + url.hash);
      }
    }
  }, []);

  useEffect(() => {
    if (isLoading) {
      const timeout = setTimeout(() => {
        sessionStorage.setItem("hasShownLoading", "true");
        setIsLoading(false);
      }, 6000);
      return () => clearTimeout(timeout);
    }
  }, [isLoading]);

  return (
    <OnboardingProvider>
      {isLoading ? (
        <TakeDeepBreath />
      ) : (
        <AppContent />
      )}
      <ToastContainer />
    </OnboardingProvider>
  );
}
